// MARK://dhk8/pos/hooks/useCatalog
// react-query hooks over the catalog (menus) client. Query keys are stable so the
// Edge cache (LAN mode) and the SPA cache stay coherent. The active menu is enriched
// with live stock (dhk8-erp-inventory) so <MenuGrid> can show LOW/OUT badges — best
// effort: if inventory is unavailable the menu still loads (stockStatus stays UNTRACKED).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getActiveMenuFlat, getMenu, listMenus, flattenMenuForGrid, setProductAvailability } from '../lib/clients/catalog.js'
import { useAuthStore } from '../stores/auth-store.js'
import { useTenantId } from './useTenantId.js'

export const catalogKeys = {
  menus: (tenantId: string | null) => ['catalog', 'menus', tenantId ?? 'no-tenant'] as const,
  menu: (tenantId: string | null, id: string) => ['catalog', 'menu', tenantId ?? 'no-tenant', id] as const,
  activeMenuFlat: (tenantId: string | null, branchId: string) =>
    ['catalog', 'menu', 'active', 'flat', tenantId ?? 'no-tenant', branchId] as const,
}

/** Raw list of menus for this tenant (each includes its sections + product placements). */
export function useMenus() {
  const tenantId = useTenantId()
  return useQuery({
    queryKey: catalogKeys.menus(tenantId),
    queryFn: ({ signal }) => listMenus({}, signal),
    enabled: !!tenantId,
    staleTime: 5 * 60_000,
  })
}

/** A single menu by id, flattened for <MenuGrid>. */
export function useMenuFlat(id: string | undefined) {
  const tenantId = useTenantId()
  return useQuery({
    queryKey: catalogKeys.menu(tenantId, id ?? ''),
    queryFn: async ({ signal }) => flattenMenuForGrid(await getMenu(id!, signal)),
    enabled: !!id && !!tenantId,
    staleTime: 60_000,
  })
}

/** The active/published menu for this tenant, flattened for <MenuGrid> + enriched with stock. */
export function useActiveMenu() {
  const tenantId = useTenantId()
  const branchId = useAuthStore((s) => s.user?.branchId)
  return useQuery({
    queryKey: catalogKeys.activeMenuFlat(tenantId, branchId ?? 'no-branch'),
    // F9.3: stock viene inline en la composición (`menuScreen` → product.availability,
    // federado de erp-inventory). Se eliminó el enrichWithStock por REST: imagen +
    // dato + inventario en UNA sola llamada del grafo (ADR-0008).
    queryFn: ({ signal }) => getActiveMenuFlat(signal),
    enabled: !!tenantId,
    staleTime: 60_000,
    refetchInterval: 60_000, // stock badges drift as orders consume inventory
  })
}

/** 86 / agotar producto: togglea `Product.active` (disponibilidad en servicio) y
 *  refresca el menú activo. El caller pasa `{ productId, available }`; al asentar,
 *  invalidamos `['catalog', 'menu', ...]` para que la grilla refleje el cambio.
 *  La UI optimista (badge "Agotado" inmediato) la maneja la página con su propio
 *  estado local — el `menuScreen` no expone `active` hoy, así que el invalidate es
 *  best-effort (mantiene coherente el resto del cache de catálogo). */
export function useSetProductAvailability() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ productId, available }: { productId: string; available: boolean }) =>
      setProductAvailability(productId, available),
    onSettled: () => { void qc.invalidateQueries({ queryKey: ['catalog', 'menu'] }) },
  })
}
